import Data from '@/data'
import { ItemFlags } from '@/data/enums'
import { GemDefinition, ModDefinition, Range, TranslationRange } from '@/data/types'

export const getValue = (value: number | Range, side: 'min' | 'max') =>
  typeof value === 'object' ? value[side] : value

/**
 * Convert value (number or range) to range.
 * @param {number|range} value
 */
export function range(value: number | Range) {
  return typeof value === 'object' ? value : { min: value, max: value }
}

export function conditionOverlap(cond: TranslationRange, value: number | Range) {
  if (typeof value !== 'object') {
    const negative = cond.negated ? 1 : 0
    if (cond.min != null && value < cond.min) return negative
    if (cond.max != null && value > cond.max) return negative
    return 1 - negative
  } else {
    const min = cond.min != null ? Math.max(cond.min, value.min) : value.min
    const max = cond.max != null ? Math.min(cond.max, value.max) : value.max
    const overlap = max - min + 1
    return cond.negated ? value.max - value.min + 1 - overlap : overlap
  }
}

/**
 * Check if value satisfies the condition
 * @param {object} cond Condition object with min/max fields
 * @param {number|object} value Single value or min/max object
 * @param {boolean} [strict=false] If the value is a range, it must be fully inside the condition range. By default, intersection is enough.
 */
export function checkCondition(cond: TranslationRange, value: number | Range, strict = false) {
  const negative = !!cond.negated
  if (typeof value !== 'object') {
    if (cond.min != null && value < cond.min) return negative
    if (cond.max != null && value > cond.max) return negative
  } else if (strict) {
    if (cond.min != null && value.min < cond.min) return negative
    if (cond.max != null && value.max > cond.max) return negative
  } else {
    if (cond.min != null && value.max < cond.min) return negative
    if (cond.max != null && value.min > cond.max) return negative
  }
  return !cond.negated
}

/**
 * Clamps a number or range to condition
 * @param {object} cond Condition object with min/max fields
 * @param {number|object} value Single value or min/max object
 */
export function forceCondition(cond: TranslationRange, value: number): number
export function forceCondition(cond: TranslationRange, value: Range): Range
export function forceCondition(cond: TranslationRange, value: number | Range): number | Range
export function forceCondition(cond: TranslationRange, value: number | Range) {
  if (typeof value === 'object') {
    if (cond.min != null) {
      if (value.min < cond.min) value = { ...value, min: cond.min }
      if (value.max < cond.min) value = { ...value, max: cond.min }
    }
    if (cond.max != null) {
      if (value.min > cond.max) value = { ...value, min: cond.max }
      if (value.max > cond.max) value = { ...value, max: cond.max }
    }
  } else {
    if (cond.min != null && value < cond.min) value = cond.min
    if (cond.max != null && value > cond.max) value = cond.max
  }
  return value
}

export type StatMap<T extends number | Range = number | Range> = Record<string, T>

/**
 * Combine (add) two or more stat maps
 * @param {object|null} dst Output stat map {id: value|range}
 * @param {object[]} src Input stat map(s)
 */
export function statsExtend(dst: StatMap<number>, ...srcs: (StatMap<number> | undefined)[]): StatMap<number>
export function statsExtend(dst: StatMap, ...srcs: (StatMap | undefined)[]): StatMap
export function statsExtend(dst: StatMap, ...srcs: (StatMap | undefined)[]) {
  if (!dst) dst = {}
  for (const src of srcs) {
    if (!src) continue
    for (const [id, value] of Object.entries(src)) {
      const prev = dst[id]
      if (!prev) {
        dst[id] = value
      } else if (typeof value === 'object' || typeof prev === 'object') {
        const a = range(value),
          b = range(prev)
        dst[id] = { min: a.min + b.min, max: a.max + b.max }
      } else {
        dst[id] = (prev || 0) + value
      }
    }
  }
  return dst
}

/**
 * Combine (add) two or more stat maps
 * @param {object} src Input stat map
 * @param {number} factor
 */
export function statsScale(src: StatMap<number>, factor: number): StatMap<number>
export function statsScale(src: StatMap, factor: number): StatMap
export function statsScale(src: StatMap, factor: number) {
  const dst: StatMap = {}
  for (const [id, value] of Object.entries(src)) {
    if (typeof value === 'object') {
      dst[id] = { min: value.min * factor, max: value.max * factor }
    } else {
      dst[id] = value * factor
    }
  }
  return dst
}

/**
 * Compare two stat maps
 * @param {object} lhs {id: value|range}
 * @param {object} rhs {id: value|range}
 */
export function statsSompare(lhs: StatMap, rhs: StatMap) {
  const keys = new Set([...Object.keys(lhs), ...Object.keys(rhs)].sort())
  for (let key of keys) {
    if (!lhs.hasOwnProperty(key)) return -1
    if (!rhs.hasOwnProperty(key)) return 1
    const lv = range(lhs[key]),
      rv = range(rhs[key])
    if (lv.min !== rv.min) return lv.min - rv.min
    if (lv.max !== rv.max) return lv.max - rv.max
  }
  return 0
}

/**
 * Format number or range object. All numbers are rounded to two decimal places.
 * If both ends of range are negative, the minus is carried outside the brackets.
 * @param {number|object} value single value or min/max object
 * @param {string} [format] If format is specified and contains a '+', positive values are prefixed with a plus.
 *                          If format is specified and contains a '%', all values are suffixed with a percent sign.
 */
export function formatValue(value: number | Range | string, format?: string): string {
  const suffix = format && format.includes('%') ? '%' : ''
  if (typeof value === 'object') {
    if (format === 'min') {
      return `${formatValue(value.min)}-${formatValue(value.max)}`
    } else {
      if (value.max < 0) return `-(${formatValue(-value.min)}-${formatValue(-value.max)})${suffix}`
      else
        return (
          (format && format.includes('+') && value.min > 0 ? '+(' : '(') +
          `${formatValue(value.min)}-${formatValue(value.max)})${suffix}`
        )
    }
  } else if (typeof value === 'string') {
    return value
  } else {
    return (format && format.includes('+') && value > 0 ? '+' : '') + Math.round(value * 100) / 100 + suffix
  }
}

type TagWeight =
  | {
      id: string
      value: number
    }
  | {
      tag: string
      weight: number
    }
  | {}

/**
 * Run an set of tags through an evaluator. The function supports two list formats.
 * 1. An array of objects with tag as the only key, in which case the value of the first matching key is returned.
 * 2. An array of objects with `id` field specifying the key, and `value` field specifying the value to return on match.
 *
 * Default return is 0.
 * @param {array} list
 * @param {Set} tags
 */
export function evaluateTags(list: TagWeight[], tags: Set<string>) {
  for (const entry of list) {
    if ('tag' in entry) {
      if (tags.has(entry.tag)) {
        return entry.weight
      }
    } else if ('id' in entry) {
      if (tags.has(entry.id)) {
        return entry.value
      }
    } else {
      const key = Object.keys(entry)[0]
      if (tags.has(key)) {
        return (entry as any)[key] as number
      }
    }
  }
  return 0
}

/**
 * Builds a stat map from a map of mods. All values are ranges, trying to accurately represent the range of
 * stats on an item based on mod groups.
 *
 * @param {object} mods {id: modinfo}
 */
export function statListFromModList(mods: Record<string, ModDefinition>) {
  const groups: Record<string, Record<string, Range>> = {}
  for (const { group: gid, stats } of Object.values(mods)) {
    const group = (groups[gid] = groups[gid] ?? {})
    for (const { id, min, max } of stats) {
      if (group[id]) {
        const dst = group[id]
        dst.min = Math.min(dst.min, min)
        dst.max = Math.max(dst.max, max)
      } else {
        group[id] = { min, max }
      }
    }
  }
  const stats: Record<string, Range> = {}
  for (const group of Object.values(groups)) {
    for (const [id, { min, max }] of Object.entries(group)) {
      if (stats[id]) {
        const dst = stats[id]
        dst.min = Math.min(dst.min, min)
        dst.max += max
      } else {
        stats[id] = { min, max }
      }
    }
  }
  return stats
}

/**
 * Returns if a value or range is equivalent to 0.
 */
export function isZero(value: number | Range) {
  if (typeof value === 'object') {
    return value.min === 0 && value.max === 0
  } else {
    return value === 0
  }
}

/**
 * Get mod stats as a map {id: {min, max}}
 */
export function modStats(mod: ModDefinition) {
  const stats: Record<string, Range> = {}
  for (const { id, min, max } of mod.stats) {
    stats[id] = { min, max }
  }
  return stats
}

/**
 * Get mod stats as a map {id: max}
 */
export function modStatsMax(mod: ModDefinition) {
  const stats: Record<string, number> = {}
  for (const { id, max } of mod.stats) {
    stats[id] = max
  }
  return stats
}

type NumberObject = number | string | { [key in string]: NumberObject } | undefined

export type RangeObject<T extends NumberObject> = T extends number
  ? Range | number
  : T extends string
  ? string
  : {
      [key in keyof T]: T[key] extends NumberObject ? RangeObject<T[key]> : never
    }

/**
 * Deep process two similar stat objects and create an object with min/max values.
 */
export function modMakeRange<T extends NumberObject>(lhs: T, rhs: T): RangeObject<T> {
  if (typeof lhs === 'object' || typeof rhs === 'object') {
    if (typeof lhs !== 'object') lhs = rhs
    if (typeof rhs !== 'object') rhs = lhs
    const res = {} as Record<keyof T, any>
    for (const key of new Set([...(Object.keys(lhs!) as (keyof T)[]), ...(Object.keys(rhs!) as (keyof T)[])])) {
      res[key] = modMakeRange(lhs![key] as NumberObject, rhs![key] as NumberObject)
    }
    return res as RangeObject<T>
  } else if (typeof lhs === 'number' || typeof rhs === 'number') {
    const lnum = typeof lhs === 'number' ? lhs : 0
    const rnum = typeof rhs === 'number' ? rhs : 0
    return (lnum === rnum ? lnum : { min: Math.min(lnum, rnum), max: Math.max(lnum, rnum) }) as RangeObject<T>
  } else {
    return lhs as any as RangeObject<T>
  }
}

/**
 * Determine gem color from str/dex/int tags.
 * @param {*} gem Gem item info.
 */
export function getGemColor(gem: GemDefinition) {
  if (gem.tags?.includes('strength')) {
    return 'red'
  } else if (gem.tags?.includes('dexterity')) {
    return 'green'
  } else if (gem.tags?.includes('intelligence')) {
    return 'blue'
  } else {
    return 'white'
  }
}

export type ModGroup = {
  mods: string[]
  stats: Record<string, Range>
  order: number
  is_essence_only: boolean
  influence: ItemFlags
  generation_type: string
  group: string
}

/**
 * Calculate mod groups (with same group+stat combination).
 * Returns the following object:
 *
 *    {
 *      lookup: {mod_id: group_index},
 *      groups: [
 *        {
 *          mods: [...mod_ids],
 *          stats: {stat_id: {min, max}},
 *          order: number
 *        },
 *        ...
 *      ]
 *    }
 *
 * @param {*} mods {mod_id: mod_info}
 */
export function modsGroupMods(mods: Record<string, ModDefinition>) {
  const groups: Record<string, string[]> = {}
  for (const [id, mod] of Object.entries(mods)) {
    const stats = [mod.group, mod.generation_type]
    if (mod.is_essence_only) stats.push('essence')
    if (mod.influence) stats.push(`influence${mod.influence}`)
    stats.push(...mod.stats.map((stat) => stat.id))
    stats.sort()
    const key = stats.join('.')
    groups[key] = groups[key] || []
    groups[key].push(id)
  }
  const result = {
    lookup: {} as Record<string, number>,
    groups: [] as ModGroup[]
  }
  for (const group of Object.values(groups)) {
    const stats: Record<string, Range> = {}
    for (const mid of group) {
      for (const { id, min, max } of mods[mid].stats) {
        if (stats[id]) {
          const dst = stats[id]
          dst.min = Math.min(dst.min, min)
          dst.max = Math.max(dst.max, max)
        } else {
          stats[id] = { min, max }
        }
      }
    }
    group.sort((a, b) => mods[a].required_level - mods[b].required_level)
    const mod = mods[group[0]]
    result.groups.push({
      mods: group,
      stats: stats,
      order: mod.order,
      is_essence_only: mod.is_essence_only,
      influence: mod.influence,
      generation_type: mod.generation_type,
      group: mod.group
    })
  }
  result.groups.sort((a, b) => a.order - b.order)
  result.groups.forEach((g, i) => g.mods.forEach((mod) => (result.lookup[mod] = i)))
  return result
}

export type ModParsingOptions = {
  max_affix?: number
  mods?: string[]
  ignore_groups?: boolean
}

/**
 * Find all combinations of mods that give the desired stats.
 * Returns a list of possible mod combinations as array of arrays (no values).
 * @param {object} mods Mod list received from getMods
 * @param {object} stats Stat map with values or ranges of values
 * @param {*} [options] Parse options.
 * `max_affix` - a value from 0 to 3 specifying maximum number of suffixes/prefixes on an item.
 * `mods` - array of known item mods.
 */
export function parseMods(mods: Record<string, ModDefinition>, stats: StatMap, options?: ModParsingOptions) {
  options = options || {}
  const max_affix = options.max_affix || 0
  // build a target vector
  const target: Record<string, Range> = {
    prefix: { min: 0, max: max_affix },
    suffix: { min: 0, max: max_affix },
    essence: { min: 0, max: 1 }
  }
  const no_group = new Set<string>()
  if (options.mods && !options.ignore_groups) {
    for (const id of options.mods) {
      const mod = Data.mods[id]
      if (mod) {
        if (mod.generation_type === 'prefix') target.prefix.max -= 1
        if (mod.generation_type === 'suffix') target.suffix.max -= 1
        no_group.add(mod.group)
      }
    }
  }
  for (const [id, value] of Object.entries(stats)) {
    if (typeof value === 'object') {
      target[id] = { ...value }
    } else {
      target[id] = { min: value, max: value }
    }
  }

  type Group = {
    mods: Record<string, Record<string, Range>>
    max: Record<string, number>
  }

  // build source vectors
  const groups: Record<string, Group> = {}
  const negative = new Set<string>()
  for (const [id, mod] of Object.entries(mods)) {
    if (no_group.has(mod.group)) continue
    if (!mod.stats.every((stat) => target[stat.id])) continue
    const modsrc: Record<string, Range> = {}
    if (!options.ignore_groups) {
      if (mod.generation_type === 'prefix') modsrc.prefix = { min: 1, max: 1 }
      if (mod.generation_type === 'suffix') modsrc.suffix = { min: 1, max: 1 }
      if (mod.is_essence_only) modsrc.essence = { min: 1, max: 1 }
    }
    for (const { id, min, max } of mod.stats) {
      modsrc[id] = { min, max }
      if (min < 0) negative.add(id)
    }
    const groupId = options.ignore_groups ? id : mod.group
    groups[groupId] = groups[groupId] ?? { mods: {}, max: {} }
    groups[groupId].mods[id] = modsrc
  }

  // we only work with positive values, so flip the negative stats
  // we assume that no stats can be both positive and negative, afaik the only
  // exception is Ventor's, which *should* work anyway (if we even parse uniques
  // with this function)
  const negate = ({ min, max }: Range) => ({ min: -max, max: -min })
  for (const id of negative) {
    target[id] = negate(target[id])
    for (const group of Object.values(groups)) {
      for (const mod of Object.values(group.mods)) {
        if (mod[id]) mod[id] = negate(mod[id])
      }
    }
  }

  // build a list of source groups and calculate their per-stat maximums
  const source: Group[] = []
  for (const group of Object.values(groups)) {
    for (const mod of Object.values(group.mods)) {
      for (const id of Object.keys(target)) {
        if (!mod[id]) mod[id] = { min: 0, max: 0 }
        group.max[id] = Math.max(group.max[id] || 0, mod[id].max)
      }
    }
    source.push(group)
  }

  // build a list of partial sums for pruning
  const reverse: Record<string, number>[] = []
  reverse[source.length] = Object.fromEntries(Object.keys(target).map((id) => [id, 0]))
  for (let i = source.length - 1; i >= 0; --i) {
    reverse[i] = { ...reverse[i + 1] }
    for (const [id, max] of Object.entries(source[i].max)) {
      reverse[i][id] += max
    }
  }

  const results: string[][] = []
  const ids = [...Object.keys(target)]

  // recursive brute-force function
  function Iterate(i: number, target: Record<string, Range>, result: string[]) {
    if (i >= source.length) {
      // end of input reached, check the result
      // this shouldn't be necessary since we did all the checks before, but just in case
      if (ids.every((id) => target[id].min <= 0 && target[id].max >= 0)) {
        results.push(result.slice())
      }
      return
    }

    const next = reverse[i + 1]
    for (const [m, src] of Object.entries(source[i].mods)) {
      // try to add the current stat
      if (ids.every((id) => src[id].min <= target[id].max && src[id].max + next[id] >= target[id].min)) {
        const sub: Record<string, Range> = {}
        for (const [id, { min, max }] of Object.entries(target)) {
          sub[id] = { min: min - src[id].max, max: max - src[id].min }
        }
        result.push(m)
        Iterate(i + 1, sub, result)
        result.pop()
      }
    }
    // try to skip current group
    if (ids.every((id) => next[id] >= target[id].min)) {
      Iterate(i + 1, target, result)
    }
  }

  Iterate(0, target, [])

  return results
}

/**
 * Failsafe for parseMods, intended for parsing implicits.
 * @param {Map} mods Mod list received from getMods
 * @param {Map} stats Stat map with values or ranges of values
 */
export function parseModsSafe(mods: Record<string, ModDefinition>, stats: StatMap) {
  const result: Record<string, Record<string, number | Range>> = {}
  stats = { ...stats }
  for (const [id, mod] of Object.entries(mods)) {
    if (mod.stats.every((stat) => stats[stat.id] != null)) {
      result[id] = {}
      for (const { id: sid } of mod.stats) {
        const value = stats[sid]
        delete stats[sid]
        result[id][sid] = value
      }
    }
  }
  return result
}

/**
 * Fit specific mod stats, based on an array of `mods` and `stats` object.
 * Assumes that the stats are in valid range.
 * @param {string[]} mods Array of mod id's.
 * @param {Object} input Input stats for `parseMods`.
 */
export function parseModStats(mods: string[], input: Record<string, number>) {
  const totals: Record<string, Range> = {}
  for (const mod of mods) {
    for (const { id, min, max } of Data.mods[mod].stats) {
      const dst = (totals[id] = totals[id] ?? { min: 0, max: 0 })
      dst.min += min
      dst.max += max
    }
  }
  input = { ...input }
  const result: Record<string, Record<string, number>> = {}
  for (const mod of mods) {
    result[mod] = {}
    const out = result[mod]
    for (const { id, min, max } of Data.mods[mod].stats) {
      const total = totals[id]
      if (total.min === total.max) {
        out[id] = min
      } else {
        out[id] = Math.round(min + (((input[id] ?? 0) - total.min) * (max - min)) / (total.max - total.min))
      }
      input[id] -= out[id]
      total.min -= min
      total.max -= max
    }
  }
  return result
}

/**
 * Attempt to factor the results from `parseMods` into subgroups.
 * Returns array of arrays of arrays of mod id's.
 * @param {*} mods Result from `parseMods`.
 */
export function simplifyMods(mods: string[][]) {
  if (!mods.length) return mods
  mods = mods.map((m) => m.slice())

  type StringArr = string | StringArr[]

  // deep compare two arrays (assuming they're sorted)
  function array_compare(a: StringArr, b: StringArr): number {
    const ta = typeof a,
      tb = typeof b
    if (ta !== tb) return ta.localeCompare(tb)
    if (ta === 'object') {
      if (a.length !== b.length) return a.length - b.length
      for (let i = 0; i < a.length; ++i) {
        const cmp = array_compare(a[i], b[i])
        if (cmp) return cmp
      }
      return 0
    } else if (ta === 'string') {
      return (a as string).localeCompare(b as string)
    } else {
      return a === b ? 0 : a < b ? -1 : 1
    }
  }

  // sort all mod combinations for future comparisons
  const maxLength = Math.max(...mods.map((m) => m.length))
  for (const modlist of mods) {
    while (modlist.length < maxLength) {
      modlist.push('')
    }
    modlist.sort(array_compare)
  }

  // set-difference of two sorted arrays
  const array_diff = (a: string[], b: string[]) => a.filter((x) => !b.includes(x))

  // find all co-factors of `f` (list of mods),
  // i.e. co-factors for AB in [ABCD, ABCE] would be [CD, CE]
  const find_cofactors = (mods: string[][], f: string[]) =>
    mods
      .filter((mod) => array_diff(f, mod).length === 0)
      .map((mod) => array_diff(mod, f))
      .sort(array_compare)

  // test if `f` can be a term in a factor of `mods`
  // to do so, we find the co-factors of `f`, and then check that all their
  // co-factors are the same
  // returns `mods` broken up into two factors if successful, or undefined otherwise
  function test_factor(mods: string[][], f: string[]) {
    const co = find_cofactors(mods, f)
    if (!co.length) return
    const fac = find_cofactors(mods, co[0])
    if (fac.length * co.length !== mods.length) return
    for (let i = 1; i < co.length; ++i) {
      if (array_compare(fac, find_cofactors(mods, co[i]))) return
    }
    return [fac, co]
  }

  // bit count of `x`
  function bit_pop(x: number) {
    x -= (x >>> 1) & 0x55555555
    x = ((x >>> 2) & 0x33333333) + (x & 0x33333333)
    x = ((x >>> 4) + x) & 0x0f0f0f0f
    x = ((x >>> 8) + x) & 0x00ff00ff
    return ((x >>> 16) + x) & 0x0000ffff
  }

  // select a subset of `a` based on bitmask `x`
  const select_set = (a: string[], x: number) => a.filter((val, index) => (x & (1 << index)) !== 0)

  const result: string[][][] = []
  // while there are any mods left, find the smallest factor
  while (mods[0].length) {
    const subs = []
    for (let idx = 1; idx < 1 << mods[0].length; ++idx) {
      subs.push(idx)
    }
    // sort all subsets by their size
    subs.sort((a, b) => bit_pop(a) - bit_pop(b))
    let split: string[][][] | undefined
    // and check if any of them can be terms
    for (let i = 0; i < subs.length; ++i) {
      split = test_factor(mods, select_set(mods[0], subs[i]))
      if (split) break
    }
    if (!split) break // should be impossible, because last option is the entire set
    result.push(split[0].map((arr) => arr.filter((a) => a.length !== 0)))
    mods = split[1]
  }

  // first list all groups with 1 option, then sort lexicographically
  result.sort((a, b) => {
    if (a.length <= 1 !== b.length <= 1) return a.length - b.length
    if (!a.length) return 0
    const am = a[0],
      bm = b[0]
    for (let i = 0; i < am.length || i < bm.length; ++i) {
      if (i >= am.length) return -1
      if (i >= bm.length) return 1
      const ma = Data.mods[am[i]],
        mb = Data.mods[bm[i]]
      if (ma.generation_type !== mb.generation_type) {
        return ma.generation_type.localeCompare(mb.generation_type)
      }
      if (ma.order !== mb.order) {
        return ma.order - mb.order
      }
    }
    return 0
  })

  return result
}

export const influenceFlags: { flag: ItemFlags; name: string }[] = [
  { flag: ItemFlags.Shaper, name: 'Shaped' },
  { flag: ItemFlags.Elder, name: 'Elder' },
  { flag: ItemFlags.Crusader, name: 'Crusader' },
  { flag: ItemFlags.Hunter, name: 'Hunter' },
  { flag: ItemFlags.Redeemer, name: 'Redeemer' },
  { flag: ItemFlags.Warlord, name: 'Warlord' },
  { flag: ItemFlags.SearingExarch, name: 'Exarch' },
  { flag: ItemFlags.EaterOfWorlds, name: 'Worlds' },
  { flag: ItemFlags.Fractured, name: 'Fractured' },
  { flag: ItemFlags.Synthesized, name: 'Synthesized' },
  { flag: ItemFlags.Veiled, name: 'Veiled' },
  { flag: ItemFlags.Archnemesis, name: 'Archnemesis' },
  { flag: ItemFlags.Experimented, name: 'Experimented' }
]
